import { useCallback, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
  giftCouponStatisticsDataAtom,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  cancelIssueCoupon,
  deleteAdditionalCoupon,
  issueCoupon,
  resetCouponToNotShared,
  restoreCouponToRequested,
  revertCouponToLaterReceive,
  updateCouponDeliveredAt,
  updateCouponDeliveryChannel,
  updateCouponPhoneAndMemo,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  useGiftCouponSearchHook,
  useGiftCouponStatisticsHook,
} from '@shared/api/giftCoupon/giftCoupon.hook';
import { useCreateGiftCsMemo } from '@shared/api/giftCsMemo/giftCsMemo.hook';
import {
  DateField,
  DeliveryChannel,
  GiftCouponForRedemption,
  GiftCouponRedeemStatus,
  GiftCouponSearchParams,
  GiftCouponShareStatus,
  formatDeliveryChannelSelectLabel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import {
  giftCouponRefundRecipientUrl,
  giftCouponRenewalRecipientUrl,
} from '@shared/utils/giftRecipientLink.util';
import { useQueryClient } from 'react-query';

import { formatDateTimeSec } from '../utils/couponSearch.utils';
import { useSessionPersistedState } from './useSessionPersistedState';
import { useToast } from './useToast';

type SortDirection = 'asc' | 'desc';

export type CouponHistorySearchHook = ReturnType<typeof useCouponHistorySearch>;

export function useCouponHistorySearch() {
  const queryClient = useQueryClient();
  useGiftCouponStatisticsHook();
  const statistics = useRecoilValue(giftCouponStatisticsDataAtom);

  // 토스트 — 공용 훅 사용 (1200ms)
  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(1200);
  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 검색 입력 상태 — 새로고침(환불 후 reload 등) 후에도 유지되도록 sessionStorage에 저장
  const [phoneNumber, setPhoneNumber] = useSessionPersistedState(
    'giftCouponHistorySearch.phoneNumber',
    '',
  );
  const [ordererName, setOrdererName] = useSessionPersistedState(
    'giftCouponHistorySearch.ordererName',
    '',
  );
  const [ordererPhone, setOrdererPhone] = useSessionPersistedState(
    'giftCouponHistorySearch.ordererPhone',
    '',
  );
  const [fullCode, setFullCode] = useSessionPersistedState(
    'giftCouponHistorySearch.fullCode',
    '',
  );
  const [dateFrom, setDateFrom] = useSessionPersistedState(
    'giftCouponHistorySearch.dateFrom',
    '',
  );
  const [dateTo, setDateTo] = useSessionPersistedState(
    'giftCouponHistorySearch.dateTo',
    '',
  );
  const [dateField, setDateField] = useSessionPersistedState<DateField>(
    'giftCouponHistorySearch.dateField',
    'requestedAt',
  );
  const [redeemStatus, setRedeemStatus] = useSessionPersistedState<
    GiftCouponRedeemStatus[]
  >('giftCouponHistorySearch.redeemStatus', []);
  const [shareStatus, setShareStatus] = useSessionPersistedState<string>(
    'giftCouponHistorySearch.shareStatus',
    '',
  );

  // 정렬
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const handleSort = useCallback(() => {
    setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
  }, []);

  // 검색 실행 상태 — 함께 저장해 새로고침 후 검색 결과까지 자동 복원
  const [searchKey, setSearchKey] = useSessionPersistedState(
    'giftCouponHistorySearch.searchKey',
    '',
  );
  const [searchParams, setSearchParams] =
    useSessionPersistedState<GiftCouponSearchParams | null>(
      'giftCouponHistorySearch.searchParams',
      null,
    );
  const [hasSearched, setHasSearched] = useSessionPersistedState(
    'giftCouponHistorySearch.hasSearched',
    false,
  );

  useGiftCouponSearchHook(searchKey, searchParams || {}, !searchKey || !searchParams);

  const status = useRecoilValue(giftCouponSearchStatusAtomFamily(searchKey));
  const searchResults = useRecoilValue(giftCouponSearchDataAtomFamily(searchKey));

  const [showBlacklist, setShowBlacklist] = useState(false);

  const handleSearch = useCallback(() => {
    if (
      !phoneNumber &&
      !ordererName &&
      !ordererPhone &&
      !fullCode &&
      !dateFrom &&
      !dateTo &&
      redeemStatus.length === 0 &&
      !shareStatus
    ) {
      alert('검색 조건을 하나 이상 입력해주세요.');
      return;
    }

    const params: GiftCouponSearchParams = {};
    if (phoneNumber) params.phoneNumber = phoneNumber.replace(/[^0-9]/g, '');
    if (ordererName) params.ordererName = ordererName;
    if (ordererPhone) params.ordererPhone = ordererPhone.replace(/[^0-9]/g, '');
    if (fullCode) params.fullCode = fullCode.trim();
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (dateFrom || dateTo) params.dateField = dateField;
    if (redeemStatus.length > 0) params.redeemStatus = redeemStatus;
    if (shareStatus) params.shareStatus = shareStatus as GiftCouponShareStatus;

    const key = JSON.stringify(params);
    setSearchKey(key);
    setSearchParams(params);
    setHasSearched(true);
  }, [
    phoneNumber,
    ordererName,
    ordererPhone,
    fullCode,
    dateFrom,
    dateTo,
    dateField,
    redeemStatus,
    shareStatus,
    setSearchKey,
    setSearchParams,
    setHasSearched,
  ]);

  const searchByStatus = useCallback(
    (status: GiftCouponRedeemStatus) => {
      setPhoneNumber('');
      setOrdererName('');
      setOrdererPhone('');
      setFullCode('');
      setDateFrom('');
      setDateTo('');
      setDateField('requestedAt');
      setShareStatus('');
      setRedeemStatus([status]);
      setTimeout(() => {
        const params: GiftCouponSearchParams = { redeemStatus: [status] };
        const key = JSON.stringify(params);
        setSearchKey(key);
        setSearchParams(params);
        setHasSearched(true);
      }, 0);
    },
    [
      setPhoneNumber,
      setOrdererName,
      setOrdererPhone,
      setFullCode,
      setDateFrom,
      setDateTo,
      setDateField,
      setShareStatus,
      setRedeemStatus,
      setSearchKey,
      setSearchParams,
      setHasSearched,
    ],
  );

  const searchByPhone = useCallback(
    (phone: string) => {
      setPhoneNumber(phone);
      setRedeemStatus([]);
      setShareStatus('');
      setOrdererName('');
      setOrdererPhone('');
      setFullCode('');
      setDateFrom('');
      setDateTo('');
      setTimeout(() => {
        const params: GiftCouponSearchParams = {
          phoneNumber: phone.replace(/[^0-9]/g, ''),
        };
        const key = JSON.stringify(params);
        setSearchKey(key);
        setSearchParams(params);
        setHasSearched(true);
      }, 0);
    },
    [
      setPhoneNumber,
      setRedeemStatus,
      setShareStatus,
      setOrdererName,
      setOrdererPhone,
      setFullCode,
      setDateFrom,
      setDateTo,
      setSearchKey,
      setSearchParams,
      setHasSearched,
    ],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  // 필터 초기화 — 입력값·검색 실행 상태를 모두 비워 sessionStorage 저장분까지 초기화
  const handleReset = useCallback(() => {
    setPhoneNumber('');
    setOrdererName('');
    setOrdererPhone('');
    setFullCode('');
    setDateFrom('');
    setDateTo('');
    setDateField('requestedAt');
    setRedeemStatus([]);
    setShareStatus('');
    setSearchKey('');
    setSearchParams(null);
    setHasSearched(false);
  }, [
    setPhoneNumber,
    setOrdererName,
    setOrdererPhone,
    setFullCode,
    setDateFrom,
    setDateTo,
    setDateField,
    setRedeemStatus,
    setShareStatus,
    setSearchKey,
    setSearchParams,
    setHasSearched,
  ]);

  // 발송매체 편집 모드
  const [channelEditMode, setChannelEditMode] = useState(false);

  const results = useMemo(() => {
    if (!searchResults) return [];
    return [...searchResults].sort((a, b) => {
      if (channelEditMode) {
        const aTime = a.gifticon?.deliveredAt
          ? new Date(a.gifticon.deliveredAt).getTime()
          : 0;
        const bTime = b.gifticon?.deliveredAt
          ? new Date(b.gifticon.deliveredAt).getTime()
          : 0;
        return bTime - aTime;
      }
      const aTime = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const bTime = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return sortDirection === 'desc' ? bTime - aTime : aTime - bTime;
    });
  }, [searchResults, sortDirection, channelEditMode]);

  // 선택 (미전달 처리용)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isUnsharing, setIsUnsharing] = useState(false);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectable = results.filter(
      (c) => c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
    );
    setSelectedIds((prev) => {
      if (selectable.length > 0 && selectable.every((c) => prev.has(c._id))) {
        return new Set();
      }
      return new Set(selectable.map((c) => c._id));
    });
  }, [results]);

  const selectedShareableCount = useMemo(() => {
    return results.filter(
      (c) =>
        selectedIds.has(c._id) && c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
    ).length;
  }, [results, selectedIds]);

  const handleUnshare = useCallback(async () => {
    const targets = results.filter(
      (c) =>
        selectedIds.has(c._id) && c.redeemStatus !== GiftCouponRedeemStatus.Delivered,
    );
    if (targets.length === 0) {
      alert('미전달 상태로 변경할 쿠폰을 선택해주세요.');
      return;
    }
    if (
      !confirm(
        `선택한 ${targets.length}건의 쿠폰을 미전달(NotShared) + 교환가능(Available) 상태로 변경하시겠습니까?`,
      )
    )
      return;

    setIsUnsharing(true);
    try {
      await Promise.all(targets.map((c) => resetCouponToNotShared(c._id)));
      showToast(`${targets.length}건 미전달 처리 완료`);
      setSelectedIds(new Set());
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      alert('미전달 처리 중 오류가 발생했습니다.');
      console.error(err);
    } finally {
      setIsUnsharing(false);
    }
  }, [results, selectedIds, showToast, queryClient]);

  // 공유상태 셀 클릭 → 단건 미전달(NotShared) + 교환가능(Available) 되돌리기.
  const handleResetShareStatusOne = useCallback(
    async (couponId: string) => {
      if (
        !confirm(
          '이 쿠폰을 미전달(NotShared) + 교환가능(Available) 상태로 변경하시겠습니까?',
        )
      )
        return;
      try {
        await resetCouponToNotShared(couponId);
        showToast('미전달 처리 완료');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        alert('미전달 처리 중 오류가 발생했습니다.');
        console.error(err);
      }
    },
    [showToast, queryClient],
  );

  // ─── 신청됨 ↔ "나중에 받기" 전환 ───
  // 수령자가 "지금은 못 쓴다(해외 체류 등), 나중에 받겠다"고 연락해 온 건.
  // 발송하지 않고 예약(Available + 수신번호) 상태로 되돌려 두면, 수령자가 받았던
  // 링크로 다시 신청할 때 발송하면 된다. recipient·verification은 지우지 않는다.
  // 전환 시 그 수신번호를 회수 잠금(additionalRecipientPhone)으로 함께 박아,
  // 다시 열린 링크로 **다른 사람이 인증·신청하는 것**을 막는다.
  const createCsMemo = useCreateGiftCsMemo();
  const [isConvertingLater, setIsConvertingLater] = useState(false);

  /** 사유를 CS 응대 메모로 남긴다. 실패(구서버 404 등)해도 상태 전환 자체는 유효 */
  const writeLaterMemo = useCallback(
    async (couponId: string, body: string): Promise<boolean> => {
      try {
        await createCsMemo.mutateAsync({ giftCoupon: couponId, body });
        return true;
      } catch (err) {
        console.error(err);
        return false;
      }
    },
    [createCsMemo],
  );

  const runLaterConvert = useCallback(
    async (
      couponId: string,
      reason: string,
      opts: {
        run: (id: string) => Promise<unknown>;
        memoPrefix: string;
        /** 메모 앞머리에 함께 남길 처리 내역 (번호 고정 등) */
        memoNote?: string;
        doneMessage: string;
        errorMessage: string;
      },
    ): Promise<boolean> => {
      setIsConvertingLater(true);
      try {
        await opts.run(couponId);
        const trimmed = reason.trim();
        const memoBody = [
          `${opts.memoPrefix}${opts.memoNote ? ` ${opts.memoNote}` : ''}`,
          trimmed,
        ]
          .filter(Boolean)
          .join('\n');
        const memoSaved = trimmed ? await writeLaterMemo(couponId, memoBody) : true;
        showToast(
          memoSaved ? opts.doneMessage : `${opts.doneMessage} (사유 메모 저장 실패)`,
        );
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
        return true;
      } catch (err) {
        alert(opts.errorMessage);
        console.error(err);
        return false;
      } finally {
        setIsConvertingLater(false);
      }
    },
    [writeLaterMemo, showToast, queryClient],
  );

  /**
   * 신청됨 → 나중에 받기 (발송대기에서 제외, 재신청 가능).
   * 신청 때 인증한 수신번호를 회수 잠금 번호로 고정해 다른 번호는 인증조차 못 하게 한다.
   */
  const handleConvertToLater = useCallback(
    (coupon: GiftCouponForRedemption, reason: string) => {
      const lockPhone = (coupon.recipient?.phoneNumber ?? '').replace(/\D/g, '');
      return runLaterConvert(coupon._id, reason, {
        run: (id) => revertCouponToLaterReceive(id, lockPhone),
        memoPrefix: '[나중에 받기 전환]',
        memoNote: lockPhone
          ? `수신번호 ${lockPhone} 고정(타번호 인증 차단)`
          : '수신번호 없음 — 번호 고정 못 함',
        doneMessage: lockPhone
          ? '나중에 받기로 전환 (번호 고정 완료)'
          : '나중에 받기로 전환했습니다.',
        errorMessage: '나중에 받기 전환 중 오류가 발생했습니다.',
      });
    },
    [runLaterConvert],
  );

  /**
   * 나중에 받기 → 신청됨 복구 (전환 오처리 되돌리기).
   * 고정해 둔 수신번호(확인번호)는 지우지 않는다 — 필요하면 표에서 직접 수정.
   */
  const handleRestoreToRequested = useCallback(
    (coupon: GiftCouponForRedemption, reason: string) =>
      runLaterConvert(coupon._id, reason, {
        run: restoreCouponToRequested,
        memoPrefix: '[발송대기 복구]',
        doneMessage: '발송대기로 되돌렸습니다.',
        errorMessage: '발송대기 복구 중 오류가 발생했습니다.',
      }),
    [runLaterConvert],
  );

  // 메모/확인번호 편집
  const [editingCouponId, setEditingCouponId] = useState<string | null>(null);
  const [editPhone, setEditPhone] = useState('');
  const [editMemo, setEditMemo] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const startEditPhoneMemo = useCallback((coupon: GiftCouponForRedemption) => {
    setEditingCouponId(coupon._id);
    setEditPhone(coupon.additionalRecipientPhone?.replace(/[^0-9]/g, '') || '');
    setEditMemo(coupon.memo || '');
  }, []);

  const cancelEditPhoneMemo = useCallback(() => {
    setEditingCouponId(null);
    setEditPhone('');
    setEditMemo('');
  }, []);

  const saveEditPhoneMemo = useCallback(async () => {
    if (!editingCouponId) return;
    setIsSavingEdit(true);
    try {
      await updateCouponPhoneAndMemo(editingCouponId, {
        additionalRecipientPhone: editPhone || '',
        memo: editMemo || '',
      });
      showToast('저장되었습니다.');
      setEditingCouponId(null);
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.';
      alert(message);
      console.error(err);
    } finally {
      setIsSavingEdit(false);
    }
  }, [editingCouponId, editPhone, editMemo, showToast, queryClient]);

  // 발송일시 편집
  const [editingDeliveredAtId, setEditingDeliveredAtId] = useState<string | null>(null);
  const [editDeliveredAt, setEditDeliveredAt] = useState('');
  const [isSavingDeliveredAt, setIsSavingDeliveredAt] = useState(false);

  const toKstDateTimeLocal = useCallback((isoString: string | Date | undefined) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '';
    const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    return kst.toISOString().slice(0, 16);
  }, []);

  const startEditDeliveredAt = useCallback(
    (coupon: GiftCouponForRedemption) => {
      setEditingDeliveredAtId(coupon._id);
      setEditDeliveredAt(toKstDateTimeLocal(coupon.gifticon?.deliveredAt));
    },
    [toKstDateTimeLocal],
  );

  const cancelEditDeliveredAt = useCallback(() => {
    setEditingDeliveredAtId(null);
    setEditDeliveredAt('');
  }, []);

  const saveEditDeliveredAt = useCallback(async () => {
    if (!editingDeliveredAtId || !editDeliveredAt) return;
    setIsSavingDeliveredAt(true);
    try {
      const isoValue = new Date(`${editDeliveredAt}:00+09:00`).toISOString();
      await updateCouponDeliveredAt(editingDeliveredAtId, isoValue);
      showToast('발송일시가 변경되었습니다.');
      setEditingDeliveredAtId(null);
      setEditDeliveredAt('');
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '발송일시 변경 중 오류가 발생했습니다.';
      alert(message);
      console.error(err);
    } finally {
      setIsSavingDeliveredAt(false);
    }
  }, [editingDeliveredAtId, editDeliveredAt, showToast, queryClient]);

  // 발송매체 일괄변경
  const [channelSelectedIds, setChannelSelectedIds] = useState<Set<string>>(new Set());
  const [bulkChannelValue, setBulkChannelValue] = useState<DeliveryChannel | ''>('');
  const [isSavingChannel, setIsSavingChannel] = useState(false);

  const deliveredGroupMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const coupon of results) {
      if (coupon.redeemStatus !== GiftCouponRedeemStatus.Delivered) continue;
      const key = coupon.gifticon?.deliveredAt
        ? formatDateTimeSec(coupon.gifticon.deliveredAt)
        : '__none__';
      const ids = map.get(key);
      if (ids) ids.push(coupon._id);
      else map.set(key, [coupon._id]);
    }
    return map;
  }, [results]);

  const toggleChannelOne = useCallback((id: string) => {
    setChannelSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleChannelGroup = useCallback(
    (groupKey: string) => {
      const ids = deliveredGroupMap.get(groupKey);
      if (!ids) return;
      setChannelSelectedIds((prev) => {
        const next = new Set(prev);
        const allSelected = ids.every((id) => next.has(id));
        if (allSelected) ids.forEach((id) => next.delete(id));
        else ids.forEach((id) => next.add(id));
        return next;
      });
    },
    [deliveredGroupMap],
  );

  const handleBulkChannelApply = useCallback(async () => {
    const ids = Array.from(channelSelectedIds);
    if (ids.length === 0) return;

    const channelLabel = bulkChannelValue
      ? formatDeliveryChannelSelectLabel(bulkChannelValue)
      : '입력안함';
    if (
      !confirm(`${ids.length}건의 발송매체를 "${channelLabel}"(으)로 변경하시겠습니까?`)
    )
      return;

    setIsSavingChannel(true);
    let successCount = 0;
    let failCount = 0;
    for (const id of ids) {
      try {
        await updateCouponDeliveryChannel(id, bulkChannelValue);
        successCount++;
      } catch (err) {
        console.error(`발송매체 변경 실패 (${id}):`, err);
        failCount++;
      }
    }
    setIsSavingChannel(false);
    setChannelSelectedIds(new Set());

    await queryClient.refetchQueries({
      predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
    });

    if (failCount === 0) {
      showToast(`${successCount}건 발송매체 변경 완료`);
    } else {
      alert(`성공: ${successCount}건, 실패: ${failCount}건`);
    }
  }, [channelSelectedIds, bulkChannelValue, showToast, queryClient]);

  const toggleChannelEditMode = useCallback(() => {
    setChannelEditMode((prev) => {
      if (prev) setChannelSelectedIds(new Set());
      return !prev;
    });
  }, []);

  // 재발행 폐기
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteAdditional = useCallback(
    async (coupon: GiftCouponForRedemption) => {
      if (
        !confirm(
          `재발행 쿠폰 [${
            coupon.fullCode || coupon.code
          }]을(를) 폐기하시겠습니까?\nDB에서 완전 삭제되며 복구할 수 없습니다.`,
        )
      )
        return;

      setDeletingId(coupon._id);
      try {
        await deleteAdditionalCoupon(coupon._id);
        showToast('재발행 쿠폰이 폐기되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '폐기 처리 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setDeletingId(null);
      }
    },
    [showToast, queryClient],
  );

  // Issue (환불/연장 링크 발행)
  const [isIssuing, setIsIssuing] = useState(false);

  // 환불 링크 안내 문자(테스트 발송 포함) — Admin·GiftAgent 공통
  const { isAdmin, isGiftAgent } = useAuth();
  const canSendRefundNotice = isAdmin() || isGiftAgent();
  const [refundNoticeTarget, setRefundNoticeTarget] = useState<{
    couponId: string;
    justIssued: boolean;
  } | null>(null);

  const openRefundNotice = useCallback((couponId: string) => {
    setRefundNoticeTarget({ couponId, justIssued: false });
  }, []);

  const closeRefundNotice = useCallback(
    async (sent: boolean) => {
      setRefundNoticeTarget(null);
      // 문자가 나갔으면 발송 기록(issued.notice*)을 새로 받는다
      if (!sent) return;
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    },
    [queryClient],
  );

  // 연장 처리 — Admin·GiftAgent 공통 (연장 링크 발행 대신 연장 기록)
  const canRecordRenewal = isAdmin() || isGiftAgent();
  const [renewalRecordTargetId, setRenewalRecordTargetId] = useState<string | null>(null);

  const openRenewalRecord = useCallback(async (couponId: string) => {
    setRenewalRecordTargetId(couponId);
  }, []);

  const closeRenewalRecord = useCallback(
    async (recorded: boolean) => {
      setRenewalRecordTargetId(null);
      // 처리되면 원본의 "연장발급됨" 표기와 새 연장쿠폰 행을 새로 받는다
      if (!recorded) return;
      showToast('연장 처리되었습니다.');
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
    },
    [queryClient, showToast],
  );

  // 발행 확인 — 버튼 한 번에 바로 발행되지 않도록 확인 모달을 거친다
  const [issueConfirmTarget, setIssueConfirmTarget] = useState<{
    couponId: string;
    type: 'refund' | 'renewal';
  } | null>(null);

  const runIssueRefund = useCallback(
    async (couponId: string) => {
      setIsIssuing(true);
      try {
        await issueCoupon({ couponId, type: 'refund' });
        const refundUrl = giftCouponRefundRecipientUrl(couponId);
        await navigator.clipboard.writeText(refundUrl);
        showToast('환불 링크가 발행되고 클립보드에 복사되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
        // 관리자는 이어서 안내 문자를 보낼 수 있게 모달을 연다
        if (canSendRefundNotice) setRefundNoticeTarget({ couponId, justIssued: true });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Issue 발행 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setIsIssuing(false);
      }
    },
    [showToast, queryClient, canSendRefundNotice],
  );

  const runIssueRenewal = useCallback(
    async (couponId: string) => {
      setIsIssuing(true);
      try {
        await issueCoupon({ couponId, type: 'renewal' });
        const renewalUrl = giftCouponRenewalRecipientUrl(couponId);
        await navigator.clipboard.writeText(renewalUrl);
        showToast('연장 링크가 발행되고 클립보드에 복사되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : '연장 링크 발행 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setIsIssuing(false);
      }
    },
    [showToast, queryClient],
  );

  const handleIssueRefund = useCallback(async (couponId: string) => {
    setIssueConfirmTarget({ couponId, type: 'refund' });
  }, []);

  const handleIssueRenewal = useCallback(async (couponId: string) => {
    setIssueConfirmTarget({ couponId, type: 'renewal' });
  }, []);

  const cancelIssueConfirm = useCallback(() => setIssueConfirmTarget(null), []);

  const confirmIssue = useCallback(async () => {
    const target = issueConfirmTarget;
    if (!target) return;
    setIssueConfirmTarget(null);
    if (target.type === 'refund') await runIssueRefund(target.couponId);
    else await runIssueRenewal(target.couponId);
  }, [issueConfirmTarget, runIssueRefund, runIssueRenewal]);

  const handleCancelIssue = useCallback(
    async (couponId: string) => {
      if (!confirm('발행된 링크를 취소하시겠습니까?')) return;
      setIsIssuing(true);
      try {
        await cancelIssueCoupon(couponId);
        showToast('링크 발행이 취소되었습니다.');
        await queryClient.refetchQueries({
          predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Issue 취소 중 오류가 발생했습니다.';
        alert(message);
        console.error(err);
      } finally {
        setIsIssuing(false);
      }
    },
    [showToast, queryClient],
  );

  return {
    // 통계
    statistics,
    showBlacklist,
    setShowBlacklist,

    // 토스트
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,

    // 검색 입력
    phoneNumber,
    setPhoneNumber,
    ordererName,
    setOrdererName,
    ordererPhone,
    setOrdererPhone,
    fullCode,
    setFullCode,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    dateField,
    setDateField,
    redeemStatus,
    setRedeemStatus,
    shareStatus,
    setShareStatus,

    // 정렬
    sortDirection,
    handleSort,

    // 검색 실행
    hasSearched,
    status,
    results,
    handleSearch,
    searchByStatus,
    searchByPhone,
    handleKeyDown,
    handleReset,

    // 선택 / 미전달 처리
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    selectedShareableCount,
    isUnsharing,
    handleUnshare,
    handleResetShareStatusOne,

    // 신청됨 ↔ 나중에 받기 전환
    isConvertingLater,
    handleConvertToLater,
    handleRestoreToRequested,

    // 메모 편집
    editingCouponId,
    editPhone,
    setEditPhone,
    editMemo,
    setEditMemo,
    isSavingEdit,
    startEditPhoneMemo,
    cancelEditPhoneMemo,
    saveEditPhoneMemo,

    // 발송일시 편집
    editingDeliveredAtId,
    editDeliveredAt,
    setEditDeliveredAt,
    isSavingDeliveredAt,
    startEditDeliveredAt,
    cancelEditDeliveredAt,
    saveEditDeliveredAt,

    // 발송매체
    channelEditMode,
    toggleChannelEditMode,
    channelSelectedIds,
    setChannelSelectedIds,
    bulkChannelValue,
    setBulkChannelValue,
    isSavingChannel,
    deliveredGroupMap,
    toggleChannelOne,
    toggleChannelGroup,
    handleBulkChannelApply,

    // 재발행 폐기
    deletingId,
    handleDeleteAdditional,

    // issue (환불/연장 링크)
    isIssuing,
    handleIssueRefund,
    handleIssueRenewal,
    handleCancelIssue,
    issueConfirmTarget,
    confirmIssue,
    cancelIssueConfirm,

    // 환불 링크 안내 문자 (Admin·GiftAgent)
    canSendRefundNotice,
    refundNoticeTarget,
    openRefundNotice,
    closeRefundNotice,

    // 연장 처리 (Admin·GiftAgent)
    canRecordRenewal,
    renewalRecordTargetId,
    openRenewalRecord,
    closeRenewalRecord,
  };
}
